/**
 * Photo upload control. Wraps a hidden file input that targets the
 * device camera by default (`capture=environment`) but also accepts
 * gallery selection — one input handles both.
 */

import { useRef, useState } from "react";

import { uploadPhotoFromFile } from "@starz/shared/photos/upload";
import type { Photo } from "@starz/shared/api/types.gen";

interface Props {
  estimateId: string;
  /** Existing photo count — used to enforce the 20-photo cap client-side. */
  currentCount: number;
  onUploaded: (photo: Photo) => void;
}

const PHOTO_CAP = 20;

export function PhotoUploadButton({ estimateId, currentCount, onUploaded }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remaining = Math.max(PHOTO_CAP - currentCount, 0);
  const disabled = remaining === 0 || busy;

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    const slice = Array.from(files).slice(0, remaining);
    for (const file of slice) {
      const result = await uploadPhotoFromFile({ estimateId, file });
      if (!result.ok) {
        setError(result.error ?? "Upload failed.");
        break;
      }
      if (result.photo) onUploaded(result.photo);
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-starz-navy px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-off-white transition-opacity hover:bg-starz-navy/90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <svg
          aria-hidden="true"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
        {busy ? "Uploading…" : "Add a photo"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        capture="environment"
        multiple
        onChange={(e) => void handleFiles(e.target.files)}
        className="hidden"
      />
      <p className="text-xs text-warm-gray">
        {remaining > 0
          ? `${currentCount}/${PHOTO_CAP} photos. JPEG or PNG, ≤ 10 MB each.`
          : `Photo limit reached (${PHOTO_CAP} per estimate).`}
      </p>
      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}
    </div>
  );
}
